/**
 * My profile, in Rowted Admin — Aaron's item 4.
 *
 * "The My Profile takes me to the user screen in the admin section - but it
 * needs to take me to a screen like the My Profile in an environment takes me
 * to where i can adjust my password and authentication."
 *
 * It did go to `/admin/admins/:id`, which is the **admin-users record**: the
 * screen another owner opens to change somebody's role, suspend them or read
 * their activity. Useful, and not a profile — there is nothing on it a person
 * can do to their own credentials, because it was built to be looked at by
 * somebody else.
 *
 * **The same cards as the office app, not a copy of them.** `SignInMethods`,
 * `EnvironmentsCard` and the devices row are the components `/account` draws;
 * what differs is one context (`AdminAccountApi`), which carries the request
 * client and this side's step-up. That is the whole of "the same user
 * management for all users" in code: one surface, two doors.
 *
 * Aaron also asked for it to be "technically separate of environment", and it
 * is as separate as the credential currently is. Today a Rowted admin account
 * and a client account are two accounts with two passwords; this screen edits
 * whichever one you are signed in to, with the same controls and the same
 * words. Making it literally one account is its own change — the credential
 * has to become one thing first — and the point of the context is that this
 * screen will not need rewriting when it does.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  ApiError, PASSWORD_MIN_LENGTH, PLATFORM_ROLE_LABELS, type MfaKind, type PlatformRole,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { adminRequest } from '../../lib/admin-api.js'
import { PageBar } from '../../frame/page-bar.js'
import { Avatar, Band, Button } from '../../components/primitives.js'
import { Modal } from '../../components/overlays.js'
import { SettingsCard } from '../../components/settings-card.js'
import { Field } from '../../components/fields.js'
import { SignInMethods, type MethodsAnswer } from '../../components/sign-in-methods.js'
import { EnvironmentsCard } from '../../components/environments-card.js'
import { TwoStepSetup } from '../../components/two-step-setup.js'
import { AdminAccountApi } from '../../account/providers.js'
import { useAccountApi } from '../../account/account-api.js'
import { SignedInDevicesRow, useDevices } from '../../account/devices-card.js'
import { useEnvironments } from '../../frame/use-environments.js'
import { IconLock } from '../../frame/icons.js'
import { AdminPage, Loading, useAdminUser } from './shell.js'

/**
 * The route — `/admin/account`, in the frame, with no rail row current.
 *
 * It is not in `ADMIN_NAV`: the way here is the account menu's **My profile**,
 * the same way the office app reaches `/account`. A seventh rail row for your
 * own profile would put it beside Clients and Settings, which are places you
 * go to do your job rather than to change your password.
 */
export function AdminAccountPage() {
  const { me, ready } = useAdminUser()
  return (
    <AdminPage me={me} active={null}>
      {!ready || !me
        ? <Loading />
        : <AdminAccount me={me} onSaved={() => window.location.reload()} />}
    </AdminPage>
  )
}

export interface AdminProfileUser {
  id: string
  name: string
  firstName?: string
  lastName?: string
  mobilePhone?: string | null
  email: string
  role: PlatformRole
}

/** What `/me/methods` answers here — the tenant shape plus two the admin needs. */
interface AdminMethodsAnswer extends MethodsAnswer {
  recoveryCodesLeft: number
  passwordChangedAt: string | null
}

export function AdminAccount({ me, onSaved }: {
  me: AdminProfileUser
  /** So the header's name redraws without a reload. */
  onSaved?: () => void
}) {
  return (
    <AdminAccountApi>
      <Inner me={me} onSaved={onSaved} />
    </AdminAccountApi>
  )
}

function Inner({ me, onSaved }: { me: AdminProfileUser; onSaved?: () => void }) {
  const { request } = useAccountApi()

  const [firstName, setFirstName] = useState(me.firstName ?? '')
  const [lastName, setLastName] = useState(me.lastName ?? '')
  const [phone, setPhone] = useState(me.mobilePhone ?? '')

  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const [methods, setMethods] = useState<AdminMethodsAnswer | null>(null)
  /* `null` is closed; a kind opens straight into that method, which is what
     **Reset** on the authenticator row means. */
  const [twoStep, setTwoStep] = useState<{ begin?: MfaKind } | null>(null)
  const [password, setPassword] = useState(false)
  const [confirmSignOut, setConfirmSignOut] = useState(false)

  const { devices, loaded: devicesLoaded, reload: reloadDevices } = useDevices()
  const { environments, current, reload: reloadEnvironments } = useEnvironments(adminRequest)

  useEffect(() => {
    /*
     * `null` stays `null` when the read fails — the Sign-in methods card draws
     * "—" rather than "Off", because telling somebody who holds a passkey that
     * they hold nothing is worse than no answer.
     */
    void request<AdminMethodsAnswer>('/me/methods')
      .then((answer) => setMethods(Array.isArray(answer?.methods) ? answer : null))
      .catch(() => setMethods(null))
  }, [request])

  /*
   * The two columns come from `GET /platform/me`, which the shell has already
   * read — no second request, and no splitting "Dana Ortiz" on a space, which
   * gets "van der Berg" wrong.
   */
  const dirty = firstName.trim() !== ''
    && (firstName !== (me.firstName ?? '')
      || lastName !== (me.lastName ?? '')
      || phone !== (me.mobilePhone ?? ''))

  const save = useCallback(async () => {
    setBusy(true)
    setProblem(null)
    try {
      await request('/me', {
        method: 'PATCH',
        body: { firstName, lastName, mobilePhone: phone.trim() || null },
      })
      setNotice('Saved.')
      onSaved?.()
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : 'That did not save.')
    } finally {
      setBusy(false)
    }
  }, [request, firstName, lastName, phone, onSaved])

  async function signOutOthers() {
    setBusy(true)
    try {
      const body = await request<{ revoked: number }>(
        '/me/sessions/revoke-others', { method: 'POST' })
      setNotice(body.revoked === 0
        ? 'You were not signed in anywhere else.'
        : `Signed out ${body.revoked} other ${body.revoked === 1 ? 'device' : 'devices'}.`)
      await reloadDevices()
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  /* Afterwards the page is reloaded rather than routed: the cookie is gone, so
     every request from here answers 401 and sign-in is the only honest next
     screen. */
  async function signOutEverywhere() {
    setBusy(true)
    try {
      await request('/me/sessions/revoke-all', { method: 'POST' })
      window.location.assign('/sign-in')
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : 'That did not work.')
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageBar
        title="My profile"
        /* Not "· Rowted Admin" as well: the black ADMIN tag in the header
           already says which app this is, and the account's own name is
           often the company's. */
        sub={[me.name, PLATFORM_ROLE_LABELS[me.role]].join(' · ')}
        actions={(
          <>
            <Button className="h-[34px]" disabled={busy} onClick={() => setConfirmSignOut(true)}>
              Sign out everywhere
            </Button>
            <Button
              variant="primary"
              className="h-[34px]"
              disabled={!dirty || busy}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        )}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex w-full max-w-[880px] flex-col gap-4 px-6 pb-6 pt-5">
          {problem && <Band tone="amber">{problem}</Band>}
          {notice && !problem && <Band tone="green">{notice}</Band>}

          {/* --- who you are ------------------------------------------- */}
          <SettingsCard title="Profile" sub="how you appear in Rowted Admin">
            <div className={cn('grid grid-cols-[88px_minmax(0,1fr)] items-start gap-[18px]',
              'px-[18px] pb-[18px] pt-4')}>
              <div className="flex justify-center">
                <Avatar name={me.name} self size={72} />
              </div>

              <div className="grid grid-cols-1 gap-x-3.5 gap-y-3 sm:grid-cols-2">
                <Field label="First name" value={firstName} onChange={setFirstName} required />
                <Field label="Last name" value={lastName} onChange={setLastName} />
                <Field
                  label="Mobile"
                  value={phone}
                  onChange={setPhone}
                  hint="So another owner can reach you about an account."
                />
                <ReadOnly
                  label="Email"
                  value={me.email}
                  /*
                   * Not editable, and the reason is worth saying: this address
                   * is how `environmentsFor` finds every place this person can
                   * sign in, and how the door finds the account at all.
                   */
                  hint="another owner changes this · Admin users"
                />
                <ReadOnly
                  label="Role"
                  value={PLATFORM_ROLE_LABELS[me.role]}
                  hint="what you may do here · set by an owner"
                />
              </div>
            </div>
          </SettingsCard>

          {/* --- how you get in ---------------------------------------- */}
          <SignInMethods
            answer={methods}
            recoveryCodesLeft={methods?.recoveryCodesLeft ?? 0}
            passwordChangedAt={methods?.passwordChangedAt ?? null}
            onChangePassword={() => setPassword(true)}
            onManage={() => setTwoStep({})}
          />

          {environments.length > 1 && (
            <EnvironmentsCard
              environments={environments}
              current={current}
              ownEmail={me.email}
              onRemoved={reloadEnvironments}
            />
          )}

          <SettingsCard title="This device">
            <SignedInDevicesRow
              devices={devices}
              loaded={devicesLoaded}
              busy={busy}
              onSignOutOthers={() => void signOutOthers()}
            />
          </SettingsCard>
        </div>
      </div>

      {confirmSignOut && (
        <Modal
          open
          onClose={() => setConfirmSignOut(false)}
          width={420}
          title="Sign out everywhere?"
          footer={(
            <>
              <Button onClick={() => setConfirmSignOut(false)}>Cancel</Button>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => { setConfirmSignOut(false); void signOutEverywhere() }}
              >
                Sign out everywhere
              </Button>
            </>
          )}
        >
          <p className="m-0 p-4 text-body leading-[1.45] text-text-muted">
            Every device signs out, including this one. Anybody with your password will
            need it again — and your second step, which Rowted Admin always asks for.
          </p>
        </Modal>
      )}

      {twoStep && (
        <TwoStepSetup
          begin={twoStep.begin}
          onClose={() => setTwoStep(null)}
          onChanged={(next) => setMethods(next as AdminMethodsAnswer)}
        />
      )}

      {password && (
        <ChangePassword
          onClose={() => setPassword(false)}
          onDone={(message) => {
            setPassword(false)
            setNotice(message)
            void reloadDevices()
          }}
        />
      )}
    </div>
  )
}

/**
 * Changing your own password.
 *
 * The current one is asked for, which is what stops a borrowed open laptop
 * being locked behind a new one; and the server revokes every other session,
 * which is the point of changing it.
 */
function ChangePassword({ onClose, onDone }: {
  onClose: () => void
  onDone: (message: string) => void
}) {
  const { request } = useAccountApi()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = current.length > 0 && next.length >= PASSWORD_MIN_LENGTH && next === again

  async function change() {
    setBusy(true)
    setError(null)
    try {
      const body = await request<{ revoked: number }>(
        '/me/password', { method: 'POST', body: { current, password: next } })
      onDone(body.revoked > 0
        ? `Password changed. ${body.revoked} other ${body.revoked === 1
          ? 'session was' : 'sessions were'} signed out.`
        : 'Password changed.')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.')
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      width={420}
      title="Change your password"
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready || busy} onClick={() => void change()}>
            {busy ? 'Changing…' : 'Change password'}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3 p-4">
        <p className="m-0 flex items-start gap-2 text-body leading-[1.45] text-text-muted">
          <span className="mt-0.5 shrink-0 text-text-faint"><IconLock size={15} /></span>
          Every other device signs out. This one stays.
        </p>
        {error && (
          <p role="alert" className="m-0 rounded-field bg-danger-bg px-3 py-2 text-cell
                                     text-danger">
            {error}
          </p>
        )}
        <Field label="Your password now" value={current} onChange={setCurrent}
          type="password" autoFocus />
        <Field label="New password" value={next} onChange={setNext} type="password"
          hint={`At least ${PASSWORD_MIN_LENGTH} characters.`} />
        <Field label="New password again" value={again} onChange={setAgain} type="password" />
      </div>
    </Modal>
  )
}

/** A field that shows rather than takes — the same box, no input. */
function ReadOnly({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="block min-w-0">
      <span className="mb-1 block text-sub text-text-muted">{label}</span>
      <span className={cn('flex h-9 w-full items-center rounded-field border border-line',
        'bg-surface px-2.5 text-[13.5px] font-semibold text-text')}>
        <span className="min-w-0 truncate">{value}</span>
      </span>
      {hint && <span className="mt-1 block text-sub text-text-faint">{hint}</span>}
    </div>
  )
}
